import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { formatDate } from "@/i18n";
import { ArrowRight, Newspaper, Users, Image as ImageIcon } from "lucide-react";
import Homepic from "../../assets/Homepic.png";

export default function Home() {
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/public/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => { });
    api.get("/public/members").then((r) => setMembers(r.data.slice(0, 4))).catch(() => { });
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden min-h-screen">
        <img
          src={Homepic}
          alt="Hintergrund"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        <div className="absolute inset-0 bg-black/45 z-10" />

        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-beige-light mb-6">
              {t("home.heroEyebrow")}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
              {t("home.heroTitle1")}<br />
              <span className="text-beige">{t("home.heroTitle2")}</span>
            </h1>
          </div>
        </div>
      </section>

      <div className="divider-line" />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">{t("home.newsEyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">{t("home.newsTitle")}</h2>
          </div>
          <Link to="/news" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-bordeaux hover:gap-3 transition-all">
            {t("common.viewAll")} <ArrowRight size={14} />
          </Link>
        </div>
        {news.length === 0 ? (
          <EmptyState icon={<Newspaper size={20} />} text={t("home.newsEmpty")} />
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {news.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                data-testid={`home-news-${n.id}`}
                className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-bordeaux transition-colors"
              >
                <div className="aspect-[4/3] bg-beige overflow-hidden">
                  {n.image_path ? (
                    <img
                      src={fileUrl(n.image_path)}
                      alt={n.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-400">
                      <Newspaper size={28} />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                    {formatDate(n.publish_at)}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-bordeaux transition-colors leading-snug break-words">
                    {n.title}
                  </h3>
                  {n.excerpt && (
                    <p className="text-sm text-slate-600 mt-3 line-clamp-3 break-words">{n.excerpt}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="divider-line" />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">{t("home.membersEyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              {t("home.membersTitle")}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              {t("home.membersSubtitle")}
            </p>
            <Link
              to="/mitglieder"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-bordeaux hover:gap-3 transition-all"
            >
              {t("home.membersAll")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-8">
            {members.length === 0 ? (
              <EmptyState icon={<Users size={20} />} text={t("home.membersEmpty")} />
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {members.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`home-member-${m.id}`}
                    className="bg-white border border-slate-200 rounded-sm p-5 flex items-center gap-4 hover:border-bordeaux transition-colors"
                  >
                    <div className="w-16 h-16 rounded-sm bg-beige overflow-hidden flex-shrink-0">
                      {m.image_path ? (
                        <img src={fileUrl(m.image_path)} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-slate-400">
                          <Users size={18} />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{m.name}</div>
                      <div className="text-xs uppercase tracking-widest text-bordeaux-accent mt-0.5">{m.position}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-bordeaux text-beige-light">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-3 items-center gap-8">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-beige mb-3">{t("home.galleryEyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("home.galleryTitle")}
            </h2>
            <p className="mt-4 text-beige/80 max-w-xl">
              {t("home.gallerySubtitle")}
            </p>
          </div>
          <div className="md:text-right">
            <Link
              to="/galerie"
              className="inline-flex items-center gap-2 bg-beige-light text-bordeaux hover:bg-white rounded-sm px-6 py-3 font-medium transition-colors"
              data-testid="home-cta-gallery"
            >
              {t("home.galleryCta")} <ImageIcon size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="border border-dashed border-slate-200 rounded-sm py-16 text-center text-slate-500">
      <div className="grid place-items-center text-slate-400 mb-3">{icon}</div>
      <p className="text-sm">{text}</p>
    </div>
  );
}
